import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { VetaCenterEnvironment } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import SelectInput from '~components/FormFields/v2/SelectInput';
import TextInput from '~components/FormFields/v2/TextInput';
import { SmallLoadingButton } from '~components/Order/ordersDispatchStyledComponents';
import { VetaCenterConnection } from '~hooks/useVetaCenter/models';
import useVetaCenter from '~hooks/useVetaCenter/useVetaCenter';
import useSingleFlight from '~utils/hooks/useSingleFlight';

import readVetaCenterRejection from './readVetaCenterRejection';

export const environmentLabelKeys = {
  [VetaCenterEnvironment.PRODUCTION]:
    'administration.integration.veta_center.environment.production',
  [VetaCenterEnvironment.TEST]: 'administration.integration.veta_center.environment.test',
} satisfies Record<VetaCenterEnvironment, string>;

const environmentIsSelectable = {
  [VetaCenterEnvironment.PRODUCTION]: true,
  [VetaCenterEnvironment.TEST]: true,
} satisfies Record<VetaCenterEnvironment, boolean>;

const SELECTABLE_ENVIRONMENTS = Object.values(VetaCenterEnvironment).filter(
  (environment) => environmentIsSelectable[environment],
);

const connectSchema = yup.object().shape({
  environment: yup
    .mixed<VetaCenterEnvironment>()
    .oneOf(SELECTABLE_ENVIRONMENTS)
    .required(),
  servicePassword: yup.string().required(
    t('form_validation_errors.required', {
      field: t('administration.integration.veta_center.form.service_password_label'),
    }),
  ),
  serviceUser: yup
    .string()
    .trim()
    .required(
      t('form_validation_errors.required', {
        field: t('administration.integration.veta_center.form.service_user_label'),
      }),
    ),
});

type ConnectDto = yup.InferType<typeof connectSchema>;

const rejectedFieldNames: Partial<Record<string, keyof ConnectDto>> = {
  service_password: 'servicePassword',
  service_user: 'serviceUser',
};

interface VetaCenterConnectFormProps {
  companyId: string;
  onConnected: (vetaCenterConnection: VetaCenterConnection) => Promise<void>;
}

export default function VetaCenterConnectForm({
  companyId,
  onConnected,
}: VetaCenterConnectFormProps) {
  const { createConnection } = useVetaCenter();
  const { isInFlight: isConnecting, runSingleFlight } = useSingleFlight();

  const { control, handleSubmit, setError } = useForm<ConnectDto>({
    resolver: yupResolver(connectSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      environment: VetaCenterEnvironment.TEST,
      servicePassword: '',
      serviceUser: '',
    },
  });

  const handleConnect = handleSubmit((formData) =>
    runSingleFlight(async () => {
      try {
        const created = await createConnection(companyId, formData);
        if (created) await onConnected(created);
      } catch (error) {
        const { field, message } = readVetaCenterRejection(error);
        const rejectedField = rejectedFieldNames[field ?? ''];
        setError(rejectedField ?? 'servicePassword', {
          message:
            message ??
            t(
              rejectedField
                ? 'error_messages.veta_center.connection_rejected'
                : 'error_messages.veta_center.failed_to_create_connection',
            ),
        });
      }
    }),
  );

  return (
    <Box
      component="form"
      noValidate
      onSubmit={handleConnect}
      sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: '560px' }}
    >
      <TextInput
        control={control}
        dataUi="veta-center-service-user"
        isDisabled={isConnecting}
        isRequired
        label={t('administration.integration.veta_center.form.service_user_label')}
        name="serviceUser"
      />

      <TextInput
        control={control}
        dataUi="veta-center-service-password"
        slotProps={{ htmlInput: { type: 'password' } }}
        isDisabled={isConnecting}
        isRequired
        label={t('administration.integration.veta_center.form.service_password_label')}
        name="servicePassword"
      />

      <SelectInput
        control={control}
        isDisabled={isConnecting}
        isRequired
        label={t('administration.integration.veta_center.form.environment_label')}
        name="environment"
        options={SELECTABLE_ENVIRONMENTS.map((environment) => ({
          label: t(environmentLabelKeys[environment]),
          value: environment,
        }))}
      />

      <Typography sx={{ color: 'text.secondary', fontSize: 12, mt: -2 }}>
        {t('administration.integration.veta_center.form.environment_helper')}
      </Typography>

      <Box sx={{ display: 'flex' }}>
        <SmallLoadingButton color="brandV2Yellow" loading={isConnecting} type="submit">
          {t('administration.integration.veta_center.cta.connect')}
        </SmallLoadingButton>
      </Box>
    </Box>
  );
}
